// Dartmouth College: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Dartmouth's Common Data Set 2025-2026 (cds_2025-26.pdf, linked from the
//    Office of Institutional Research's Common Data Set page): C1 applicants,
//    admits and enrollees for Fall 2025, C8 test policy for Fall 2027,
//    C21/C22 plans with the early decision counts. The C1 residency table
//    fills in applicants and enrollees by residency but leaves admits blank,
//    so no residency table is shown. Quirk: G1 is marked "not available at
//    this time" for 2026-2027, so cost comes from the financial aid office.
//  - admissions.dartmouth.edu: Apply to Dartmouth (the Early Decision and
//    Regular Decision timelines and required components), the Early
//    Decision, Regular Decision and Portfolio glossary entries, and the
//    Testing Policy page. Quirk: one QuestBridge paragraph on the Apply page
//    gives a January 2 Regular Decision deadline; the Regular Decision
//    timeline and the glossary both say January 1, which is used here.
//  - Dartmouth Admissions, Cost of Attendance 2026-2027. Its own total
//    ($98,946) equals the sum of its items and includes the $519 first-year
//    orientation fee.
//  - College Scorecard, school id 182670 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Dartmouth Common Data Set 2025-2026',
        url: 'https://www.dartmouth.edu/oir/pdfs/cds_2025-26.pdf',
        read: '2026-10-01',
    },
    apply: {
        label: 'Dartmouth Admissions, Apply to Dartmouth',
        url: 'https://admissions.dartmouth.edu/apply/apply-dartmouth',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Dartmouth Admissions Glossary, Early Decision',
        url: 'https://admissions.dartmouth.edu/glossary-term/early-decision',
        read: '2026-10-01',
    },
    portfolio: {
        label: 'Dartmouth Admissions Glossary, Portfolio',
        url: 'https://admissions.dartmouth.edu/glossary-term/portfolio',
        read: '2026-10-01',
    },
    tests: {
        label: 'Dartmouth Admissions, Testing Policy',
        url: 'https://admissions.dartmouth.edu/apply/testing-policy',
        read: '2026-10-01',
    },
    costs: {
        label: 'Dartmouth Admissions, Cost of Attendance 2026-2027',
        url: 'https://admissions.dartmouth.edu/afford/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?182670-Dartmouth-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'dartmouth-college',
    kbId: 'dartmouth_college',
    name: 'Dartmouth College',
    shortName: 'Dartmouth',
    location: 'Hanover, NH',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Dartmouth Communications, Dartmouth
    // colors); decoration only.
    branding: {
        primary: '#00693E',
        secondary: '#12312B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/dartmouth_college_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 28230,
        admitted: 1699,
        enrolled: 1205,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Dartmouth\'s Common Data Set reports one first-year acceptance rate for the whole college, with no rate by major. Students do not declare a major until sophomore year.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision', applied: 3552, admitted: 768 },
        ],
        note: 'The Common Data Set counts for the fall 2025 entering class. They are part of the totals above. Dartmouth has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Every first-year applicant sends the same parts through the Common App. One addition is optional:',
        rows: [
            { school: 'Fine and performing arts', requirement: 'Applicants who wish to show extraordinary talent in the fine or performing arts may submit a portfolio (fine art, recordings, performance videos) through the Application Portal after submitting the rest of the application: within one week of the Early Decision deadline, or two weeks of the Regular Decision deadline.', source: 'portfolio' },
        ],
        note: 'Required for everyone: the Common App, the Dartmouth Writing Supplement, the Secondary School Report with transcript, school profile and counselor evaluation, two teacher evaluations, and the SAT or ACT. A peer recommendation is recommended.',
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'Mid December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'Late March or early April', binding: false },
        ],
        note: 'Applications go live August 1. Financial aid materials are due November 1 for Early Decision and February 1 for Regular Decision. Early Decision applicants who are deferred hear on or before April 1. Admitted Regular Decision students reply by May 1.',
        source: 'apply',
        binding: 'Early Decision is binding: you sign the Early Decision Agreement and commit to attend Dartmouth if admitted (barring extenuating circumstances such as unforeseen financial hardship). You may apply elsewhere through early programs that are neither restricted nor binding, but you may not submit binding early applications to other colleges, and you withdraw your other applications if admitted.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Standardized testing is a required part of Dartmouth\'s application. Students at high schools in the United States must send the SAT or ACT; Dartmouth has no preference between them and superscores, using the highest result on each section across test dates. Students at high schools outside the United States may instead send three AP exams, IB or A-Level results, or a national exam.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 71697 },
            { item: 'Fees (including the first-year orientation fee)', amount: 2426 },
            { item: 'Housing', amount: 13032 },
            { item: 'Food', amount: 8746 },
            { item: 'Books, course materials, supplies and equipment', amount: 1005 },
            { item: 'Miscellaneous', amount: 2040 },
        ],
        total: 98946,
        note: 'For a first-year student. Not included: the required health plan unless waived ($5,216 in 2026-2027), the required computer if you buy Dartmouth\'s basic package ($1,700), and travel.',
        source: 'costs',
        netPrice: {
            average: 26707,
            byIncome: [
                { income: '$0 to $30,000', amount: 1399 },
                { income: '$30,001 to $48,000', amount: null },
                { income: '$48,001 to $75,000', amount: 1680 },
                { income: '$75,001 to $110,000', amount: 11641 },
                { income: 'Over $110,000', amount: 56894 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $30,001 to $48,000 band. Your own price comes from Dartmouth\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
